import type { CoordinateOrigin, Dependency, ScanReport } from "./types";

/**
 * The only CycloneDX revision this exporter is written against. It is declared
 * rather than assumed so the document, the test fixture schemas, and the code
 * cannot drift apart silently.
 */
export const CYCLONEDX_SPEC_VERSION = "1.6";

const UNKNOWN = "unknown";

export interface OmittedComponent {
  source: string;
  reason: string;
}

export interface CycloneDxResult {
  bom: CycloneDxBom;
  /** Components that were left out, so the gap is reportable rather than silent. */
  omitted: OmittedComponent[];
}

interface CycloneDxHash {
  alg: string;
  content: string;
}

interface CycloneDxProperty {
  name: string;
  value: string;
}

interface CycloneDxComponent {
  type: string;
  "bom-ref": string;
  name: string;
  group?: string;
  version?: string;
  purl?: string;
  hashes?: CycloneDxHash[];
  properties?: CycloneDxProperty[];
}

interface CycloneDxDependency {
  ref: string;
  dependsOn: string[];
}

export interface CycloneDxBom {
  bomFormat: "CycloneDX";
  specVersion: string;
  serialNumber: string;
  version: number;
  metadata: {
    timestamp: string;
    tools: { components: { type: string; name: string; version: string }[] };
    component: CycloneDxComponent & { description?: string };
  };
  components: CycloneDxComponent[];
  dependencies: CycloneDxDependency[];
}

function originOf(
  dependency: Dependency,
  field: "groupId" | "artifactId" | "version",
): CoordinateOrigin {
  const recorded = dependency.origins?.[field];
  if (recorded) return recorded;
  // Pre-provenance reports predate the origins field. A value equal to the
  // sentinel was never established; anything else was read from the file.
  return dependency[field] && dependency[field] !== UNKNOWN
    ? "parsed"
    : "missing";
}

/**
 * A purl is only meaningful when group, artifact and version are all known.
 * Anything else would be a guess encoded as an identifier, and a wrong purl is
 * worse than no purl because downstream tooling trusts it.
 */
function purlFor(dependency: Dependency): string | null {
  const group = dependency.groupId;
  const artifact = dependency.artifactId;
  const version = dependency.version;
  if (!group || group === UNKNOWN) return null;
  if (!artifact || artifact === UNKNOWN) return null;
  if (!version || version === UNKNOWN) return null;
  const encode = (value: string) =>
    encodeURIComponent(value).replace(/%2F/gi, "/");
  return `pkg:maven/${encode(group)}/${encode(artifact)}@${encode(version)}`;
}

function describeProvenance(
  dependency: Dependency,
  field: "groupId" | "artifactId" | "version",
): string {
  const origin = originOf(dependency, field);
  return origin === "parsed"
    ? "read from the coordinate file"
    : origin === "path"
      ? "inferred from the entry path, not stated by the file"
      : "not established";
}

/**
 * A UUID-shaped serial number derived from the artifact's own SHA-256.
 *
 * CycloneDX asks for an RFC 4122 identifier, and the export has to be
 * byte-identical across runs of the same scan, so it cannot be random. The
 * version and variant nibbles are set so the result is a well-formed UUID
 * rather than a hex string wearing a urn:uuid: prefix.
 */
function serialNumberFor(sha256: string): string {
  const hex = sha256.replace(/[^0-9a-f]/gi, "").toLowerCase().padEnd(64, "0");
  const bytes = [
    hex.slice(0, 2),
    hex.slice(2, 4),
    hex.slice(4, 6),
    hex.slice(6, 8),
    hex.slice(8, 10),
    hex.slice(10, 12),
    hex.slice(12, 14),
    hex.slice(14, 16),
    hex.slice(16, 18),
    hex.slice(18, 20),
    hex.slice(20, 22),
    hex.slice(22, 24),
    hex.slice(24, 26),
    hex.slice(26, 28),
    hex.slice(28, 30),
    hex.slice(30, 32),
  ].map((pair) => Number.parseInt(pair, 16) & 0xff);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex32 = bytes.map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return `urn:uuid:${hex32.slice(0, 8)}-${hex32.slice(8, 12)}-${hex32.slice(
    12,
    16,
  )}-${hex32.slice(16, 20)}-${hex32.slice(20, 32)}`;
}

/** Group, artifact and version alone can collide across sources; the path cannot. */
function componentRef(purl: string, dependency: Dependency): string {
  return `${purl}#${dependency.source}`;
}

function buildComponent(
  dependency: Dependency,
  purl: string,
): CycloneDxComponent {
  const groupOrigin = originOf(dependency, "groupId");
  const artifactOrigin = originOf(dependency, "artifactId");
  const versionOrigin = originOf(dependency, "version");
  const inferred = [groupOrigin, artifactOrigin, versionOrigin].some(
    (origin) => origin === "path",
  );
  const properties: CycloneDxProperty[] = [
    { name: "jarlab:coordinate-file", value: dependency.source },
    { name: "jarlab:groupId", value: describeProvenance(dependency, "groupId") },
    {
      name: "jarlab:artifactId",
      value: describeProvenance(dependency, "artifactId"),
    },
    { name: "jarlab:version", value: describeProvenance(dependency, "version") },
    // JarLab reads coordinate files, not build descriptors, so a Maven scope
    // is never established. Stating that beats leaving it looking resolved.
    { name: "jarlab:scope", value: "not established by static analysis" },
  ];
  if (inferred) {
    properties.push({
      name: "jarlab:coordinate-confidence",
      value: "partial: at least one coordinate was inferred from the entry path",
    });
  }
  return {
    type: "library",
    "bom-ref": componentRef(purl, dependency),
    name: dependency.artifactId,
    group: dependency.groupId,
    version: dependency.version,
    purl,
    properties,
  };
}

export function buildCycloneDxBom(report: ScanReport): CycloneDxResult {
  const rootRef = `jarlab:root:${report.hashes.sha256}`;
  const components: CycloneDxComponent[] = [];
  const omitted: OmittedComponent[] = [];
  const seen = new Set<string>();

  // Sorted so the document is identical for identical input regardless of the
  // order the archive happened to yield entries in.
  const ordered = [...report.dependencies].sort((left, right) =>
    `${left.source}\u0000${left.groupId}\u0000${left.artifactId}\u0000${
      left.version
    }`.localeCompare(
      `${right.source}\u0000${right.groupId}\u0000${right.artifactId}\u0000${
        right.version
      }`,
    ),
  );

  for (const dependency of ordered) {
    const purl = purlFor(dependency);
    if (!purl) {
      const missing = (["groupId", "artifactId", "version"] as const).filter(
        (field) => originOf(dependency, field) === "missing",
      );
      omitted.push({
        source: dependency.source,
        reason: `no trustworthy coordinate: ${missing.join(", ")} could not be established`,
      });
      continue;
    }
    const ref = componentRef(purl, dependency);
    if (seen.has(ref)) continue;
    seen.add(ref);
    components.push(buildComponent(dependency, purl));
  }

  const rootProperties: CycloneDxProperty[] = [
    { name: "jarlab:sha256", value: report.hashes.sha256 },
    {
      name: "jarlab:coverage",
      value:
        "Built from coordinate files found inside the artifact by static analysis. Maven scopes, transitive dependency edges and resolved build metadata were not observed and are not represented.",
    },
    { name: "jarlab:components-omitted", value: String(omitted.length) },
    // A component list that silently stops short reads as "that is
    // everything", so each omission is named in the document itself.
    ...omitted.map((entry) => ({
      name: "jarlab:omitted-coordinate",
      value: `${entry.source} — ${entry.reason}`,
    })),
  ];

  const bom: CycloneDxBom = {
    bomFormat: "CycloneDX",
    specVersion: CYCLONEDX_SPEC_VERSION,
    serialNumber: serialNumberFor(report.hashes.sha256),
    version: 1,
    metadata: {
      timestamp: report.completedAt,
      tools: {
        components: [
          {
            type: "application",
            name: "JarLab",
            version: report.analyzerVersion,
          },
        ],
      },
      component: {
        type: "application",
        "bom-ref": rootRef,
        name: report.analyzedFileName,
        hashes: [
          { alg: "SHA-256", content: report.hashes.analyzedSha256 },
          { alg: "SHA-512", content: report.hashes.sha512 },
        ],
        properties: rootProperties,
      },
    },
    components,
    // Only one relationship was actually observed: the artifact references the
    // coordinates found inside it. Nothing here claims a dependency graph
    // between the components, because none was read.
    dependencies: [
      { ref: rootRef, dependsOn: components.map((component) => component["bom-ref"]) },
      ...components.map((component) => ({
        ref: component["bom-ref"],
        dependsOn: [] as string[],
      })),
    ],
  };

  return { bom, omitted };
}

/** Stable two-space JSON. Key order is fixed by construction, not by sort. */
export function reportToCycloneDx(report: ScanReport): string {
  return `${JSON.stringify(buildCycloneDxBom(report).bom, null, 2)}\n`;
}

/**
 * One line describing what the export will actually contain, shown on the
 * download card. A component count that silently drops unresolvable
 * coordinates would read as a complete inventory.
 */
export function sbomExportDetail(report: ScanReport): string {
  const { bom, omitted } = buildCycloneDxBom(report);
  const components = bom.components.length;
  const base =
    components === 0
      ? "No resolvable Maven coordinates were found"
      : `${components} component${components === 1 ? "" : "s"} with parsed coordinates`;
  const suffix = omitted.length
    ? `${base}; ${omitted.length} omitted as unresolvable. No scope or transitive edges.`
    : `${base}. Static analysis only; no scope or transitive edges.`;
  return suffix;
}
